import Autocomplete from '@mui/material/Autocomplete'
import Checkbox from '@mui/material/Checkbox'
import CircularProgress from '@mui/material/CircularProgress'
import TextField from '@mui/material/TextField'
import { useTheme } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import { useTranslation } from 'react-i18next'

import { AdminUITheme } from 'types/AdminUiThemeMui'
import useDebounce from 'lib/hooks/UseDebounce'
import OptionWithTooltip, {
  shouldShowTooltip,
} from 'components/Shared/OptionWithTooltip'
import {
  checkOptionValueBasedOnRegion,
  fetchOptionBasedOnRegion,
  getFormattedInputValue,
} from 'components/Participants/Filters/Suburbs/Helper'
import suburbsAPI from 'api/ApiActions/Filters/Suburbs/Action'
import CustomChip from 'components/Shared/Chip'
import { useAppDispatch, useAppSelector } from 'redux/Hook'
import { FilterItem } from 'types/FilterBuilder'
import { updateFilter } from 'redux/slices/FilterBuilder'
import {
  zIndexLoweredFilterCSS,
  zIndexRaisedFilterCSS,
} from 'lib/GlobalConstants'
import { mappedSuburb } from 'lib/Helper'

type Props = {
  item: FilterItem
  index: number
}

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />
const checkedIcon = <CheckBoxIcon fontSize="small" />

const FilterBySuburbs = (props: Props) => {
  const [cssUpdates, setCssUpdates] = useState<any>(zIndexLoweredFilterCSS)
  const [options, setOptions] = useState<Array<SuburbOption>>([])
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const { item, index } = props
  const isOpenRef = useRef(false)
  const forceUpdate = React.useReducer(() => ({}), {})[1] as () => void
  const inputRef = useRef({ focus: () => null, blur: () => null })
  const site = useAppSelector((state) => state.site.site)
  const theme: AdminUITheme = useTheme()
  const debouncedSearchTerm = useDebounce(searchTerm, 500)
  const region = site.region.toLowerCase()

  useEffect(() => {
    if (debouncedSearchTerm) {
      handleFetchSuburbs()
    }
  }, [debouncedSearchTerm])

  const handleFetchSuburbs = async () => {
    setLoading(true)
    const results = await suburbsAPI.fetchSuburbs(debouncedSearchTerm)
    setOptions(results.data.data)
    setLoading(false)
  }

  const handleFilterBySuburb = (value: Array<SuburbOption>) => {
    let suburbString = ''

    if (value.length === 1) {
      suburbString = fetchOptionBasedOnRegion(region, value[0])
    } else {
      const suburbOptionsArray = []
      for (let i = 0; i < value.length; i++) {
        suburbOptionsArray.push(fetchOptionBasedOnRegion(region, value[i]))
      }
      suburbString = suburbOptionsArray.join('|')
    }

    dispatch(updateFilter({ item: { value: suburbString }, index }))
  }

  useEffect(() => {
    inputRef.current.focus()
  }, [item.value])

  const onBlur = () => {
    setCssUpdates(zIndexLoweredFilterCSS)
    isOpenRef.current = false
    forceUpdate()
    setOptions([]) // on close of the input box empty the dropdown menu
  }

  const onFocus = () => {
    setCssUpdates(zIndexRaisedFilterCSS)
    isOpenRef.current = true
    forceUpdate()
  }

  const getLabelBasedOnRegion = () => {
    switch (region) {
      case 'australia':
      case 'new_zealand':
        return t(`filters.suburbs.label`)
      case 'united_states':
        return t(`filters.suburbs.zipCode`)
      default:
        return t(`filters.suburbs.postalCode`)
    }
  }

  // This function is needed by view group functionalites to show selected suburb
  const getDefaultValues = () => {
    if (!item.value) return []

    const suburbs = item.value.split('|').map((sub: any) => {
      return mappedSuburb(sub, region)
    })

    return suburbs
  }

  return (
    <Autocomplete
      sx={{
        ...{
          '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
            pr: 3,
          },
        },
        ...theme.GenericTheme.dynamicFilterWrapperWidth,
      }}
      options={options}
      multiple
      defaultValue={getDefaultValues()}
      onFocus={onFocus}
      onBlur={onBlur}
      openText={t('filters.ariaTexts.open')}
      closeText={t('filters.ariaTexts.close')}
      clearText={t('filters.ariaTexts.clear')}
      size="small"
      limitTags={1}
      disableCloseOnSelect
      noOptionsText={t('common.noOptions')}
      // to avoid mui autocomplete's in built filtering https://mui.com/material-ui/react-autocomplete/#search-as-you-type
      filterOptions={(x) => x}
      getOptionLabel={(option: SuburbOption) =>
        fetchOptionBasedOnRegion(region, option)
      }
      isOptionEqualToValue={(option, value) => {
        return checkOptionValueBasedOnRegion(option, value, region)
      }}
      onInputChange={(_, newInputValue) => {
        // if the canadian postal code has space between and if it is present
        // then it also should show in the result.
        const formattedInputValue = getFormattedInputValue(
          region,
          newInputValue,
        )

        setSearchTerm(formattedInputValue)
      }}
      onChange={(_, value) => handleFilterBySuburb(value)}
      renderInput={(params) => (
        <TextField
          {...params}
          style={{
            ...theme.GenericTheme.textFieldCSSToRaiseZindex,
            ...cssUpdates,
            ...theme.GenericTheme.dynamicFilterWrapperWidth,
          }}
          label={getLabelBasedOnRegion()}
          placeholder={t(`filters.search`)}
          fullWidth
          data-testid="filter-by-suburb"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
      renderTags={(tagValue: SuburbOption[], getTagProps: any) => {
        return tagValue.map((option, index) => {
          const suburbTagValue = fetchOptionBasedOnRegion(region, option)
          return (
            <CustomChip
              key={suburbTagValue}
              filterValues={[suburbTagValue]}
              size="small"
              extraProps={{
                ...getTagProps({ index }),
                color: 'primary',
              }}
            />
          )
        })
      }}
      renderOption={(props, option: SuburbOption, state) => {
        const suburbOption = fetchOptionBasedOnRegion(region, option)
        return (
          <li {...props}>
            <Checkbox
              value={fetchOptionBasedOnRegion(region, option)}
              icon={icon}
              checkedIcon={checkedIcon}
              checked={state.selected}
            />
            {shouldShowTooltip(suburbOption) ? (
              <OptionWithTooltip option={suburbOption} />
            ) : (
              suburbOption
            )}
          </li>
        )
      }}
    />
  )
}

export default FilterBySuburbs
